import { IconChevronDown, IconSearch } from "@tabler/icons-react";
import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui";
import { cn } from "@/lib/cn";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { useBuscarClavesSat, useClavesSatConsultadas } from "../hooks/use-claves-sat";

const ALTURA = { xs: "h-7 text-xs", sm: "h-8 text-sm", md: "h-9 text-sm" } as const;

/**
 * Selector de clave producto/servicio SAT con búsqueda EN EL SERVIDOR (el catálogo del SAT tiene ~52 mil claves ·
 * un `<Select>` con todas las opciones ya no es viable). Busca por clave, descripción o palabras similares, sin
 * distinguir acentos ni mayúsculas, y solo ofrece las claves utilizables hoy.
 */
export function ClaveSatPicker({
  value,
  onChange,
  label,
  error,
  size = "sm",
  className,
  wrapperClassName,
}: {
  value: string;
  onChange: (clave: string) => void;
  label?: string;
  error?: string;
  size?: keyof typeof ALTURA;
  className?: string;
  wrapperClassName?: string;
}) {
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState("");
  const [busqueda] = useDebouncedValue(texto, 300);
  const { data, isFetching } = useBuscarClavesSat(busqueda, abierto);
  const actual = useClavesSatConsultadas(value ? [value] : []);

  const seleccionar = (clave: string) => {
    onChange(clave);
    setAbierto(false);
    setTexto("");
  };

  return (
    <div className={cn("flex flex-col gap-1", wrapperClassName)}>
      {label && <span className="text-sm font-medium text-foreground">{label}</span>}
      <Popover open={abierto} onOpenChange={setAbierto}>
        <PopoverTrigger asChild>
          <button
            type="button"
            title={value ? `${value} · ${actual.descripcionDe(value) ?? ""}` : undefined}
            className={cn(
              "flex w-full items-center justify-between gap-2 rounded-md border bg-background px-3 font-mono",
              "focus:outline-none focus:ring-2 focus:ring-multiva-600/30 focus:border-multiva-600",
              error ? "border-peligro-600" : "border-input",
              ALTURA[size],
              className
            )}
          >
            <span className={cn("min-w-0 flex-1 truncate text-left", !value && "font-sans text-muted-foreground")}>
              {value || "Busca una clave…"}
            </span>
            <IconChevronDown size={14} className="shrink-0 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[26rem] max-w-[90vw] p-0">
          <div className="flex items-center gap-1.5 border-b border-border px-2 py-1.5">
            <IconSearch size={14} className="shrink-0 text-muted-foreground" />
            <input
              autoFocus
              value={texto}
              onChange={(e) => setTexto(e.currentTarget.value)}
              placeholder="Clave, descripción o palabra similar…"
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="max-h-72 overflow-y-auto p-1">
            {(data?.items ?? []).length === 0 ? (
              <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                {data === undefined || isFetching ? "Buscando…" : "Sin resultados."}
              </p>
            ) : (
              data!.items.map((c) => (
                <button
                  key={c.clave}
                  type="button"
                  onClick={() => seleccionar(c.clave)}
                  className={cn(
                    "flex w-full flex-col rounded-sm px-2 py-1.5 text-left hover:bg-muted focus:bg-muted focus:outline-none",
                    c.clave === value && "bg-muted"
                  )}
                >
                  <span className="font-mono text-xs font-semibold">{c.clave}</span>
                  <span className="text-xs text-muted-foreground">{c.descripcion}</span>
                </button>
              ))
            )}
          </div>
          {data && data.total > data.items.length && (
            <p className="border-t border-border px-2 py-1.5 text-[11px] text-muted-foreground">
              Mostrando {data.items.length} de {data.total.toLocaleString("es-MX")} · sigue escribiendo para acotar.
            </p>
          )}
        </PopoverContent>
      </Popover>
      {error && <p className="text-xs text-peligro-600">{error}</p>}
    </div>
  );
}
